import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Switch,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import {
	Contact,
	useUserByContactIdLazyQuery,
	useUserRemoveLoginMutation,
	useGetAllContactsLazyQuery,
	User,
	UserGroup,
	useAccessibleUserGroupsLazyQuery,
} from "@repo/server-api";
import { EmailInformation, OptionType } from "@repo/server-api/types";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import * as Yup from "yup";

import { setContactValues } from "@repo/legacy/components/helper/Index";

import Dropdown from "./Dropdown";
import ConformationDialog from "../dialogs/Conformation";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const UserSection = forwardRef(
	(
		props: Readonly<{
			contact: Contact | null;
			errors?: Record<string, boolean | object | string>;
			setErrors?: (value: Record<string, boolean | object | string>) => void;
			isDialog?: boolean;
			user: User | null;
			setUser?: (value: User) => void;
		}>,
		ref: React.Ref<{
			validate: () => Promise<Record<string, boolean | object | string>>;
		}>,
	) => {
		const { t } = useTranslation();

		const { showNotification } = useNotification();

		const [openDeleteConformation, setOpenDeleteConformation] = useState<boolean>(false);

		const [client, setClient] = useState<OptionType | null>({
			title: "",
			inputValue: "",
			item: null,
		});
		const [contacts, setContacts] = useState<Contact[]>([]);

		const validationSchema = Yup.object().shape({
			username: Yup.string().required(t("username.required", { ns: "validation" })),
			groups: Yup.array().min(1, t("groups.required", { ns: "validation" })),
		});

		const [groups, setGroups] = useState<UserGroup[]>([]);

		const [getUserByContactId] = useUserByContactIdLazyQuery({
			variables: { id: props.contact?.id ?? "" },
			fetchPolicy: "no-cache",
			onCompleted: (data) => {
				if (data && data.userByContactId) {
					const userGroups = groups
						.filter((group) => data.userByContactId?.groups?.includes(group.name))
						.map((group: UserGroup) => group.id);
					props.setUser?.({
						...data.userByContactId,
						groups: userGroups,
					});
				}
			},
		});

		const [getContacts] = useGetAllContactsLazyQuery({
			variables: { first: 10, after: null, name: "" },
			fetchPolicy: "no-cache",
			onCompleted(data) {
				if (data && data.allContacts?.nodes) {
					const tmpContacts = data.allContacts.nodes.map((contact) => ({
						...setContactValues(contact as unknown as Contact),
					}));
					setContacts(tmpContacts);
				}
			},
		});

		const [getAccesableUserGroups] = useAccessibleUserGroupsLazyQuery({
			onCompleted: (data) => {
				if (data && data.accessibleUserGroups) {
					const availableGroups = data.accessibleUserGroups.filter((group) =>
						[
							"employee",
							"freelancer",
							"freelancer_of_supplier",
							"project_manager",
							"tenant_administrator",
						].includes(group.name),
					);
					setGroups(availableGroups);
				}
			},
		});

		useEffect(() => {
			getContacts();
			getAccesableUserGroups().then(() => {
				if (props.contact?.id) getUserByContactId();
			});
		}, [getContacts, getAccesableUserGroups, getUserByContactId, props.contact?.id]);

		useEffect(() => {
			if (props.contact) {
				getAccesableUserGroups().then(() => {
					getUserByContactId();
				});
				if (props.isDialog)
					setClient({
						title: props.contact.displayName ?? "",
						inputValue: props.contact.displayName ?? "",
						item: props.contact,
					});
			}
		}, [props.contact]);

		const user = useMemo(() => {
			if (groups.length === 0) return props.user;
			const ug =
				groups
					.filter(
						(group) =>
							props.user?.groups?.includes(group.name) || props.user?.groups?.includes(group.id),
					)
					.map((group: UserGroup) => group.id) || [];
			if (props.user)
				return {
					...props.user,
					groups: ug,
				};

			return {
				contactId: null,
				email: "",
				emailVerified: false,
				enabled: false,
				groups: ug,
				id: "",
				username: "",
				firstName: "",
				lastName: "",
			};
		}, [props.user, groups]);

		useImperativeHandle(ref, () => ({
			validate: validateFields,
		}));

		const validateFields = useCallback(async () => {
			const localErrors: Record<string, boolean | object | string> = {};
			try {
				await validationSchema.validate(user, { abortEarly: false });
				props.setErrors?.({});
				return localErrors;
			} catch (err) {
				if (err instanceof Yup.ValidationError) {
					err.inner.forEach((error) => {
						if (error.path) {
							localErrors[error.path] = error.message;
						}
					});
					props.setErrors?.({ ...localErrors });
				}
				return localErrors;
			}
		}, [props]);

		useEffect(() => {
			if (client?.item) {
				const email = (client.item as Contact).additionalInformation.filter(
					(info): info is EmailInformation =>
						(info as EmailInformation).__typename === "EmailInformation",
				)[0]?.emailAddress;
				props.setUser?.({
					__typename: "User",
					id: user?.id ?? "",
					contactId: client.item?.id ?? "",
					email: email ?? "",
					firstName: (client.item as Contact)?.firstName ?? "",
					lastName: (client.item as Contact)?.lastName ?? "",
					username:
						(client.item as Contact)?.firstName?.toLowerCase() +
						"." +
						(client.item as Contact)?.lastName?.toLowerCase(),
					groups: user?.groups || [],
					emailVerified: user?.emailVerified || false,
					enabled: user?.enabled || false,
				});
				props.setErrors?.({ ...props.errors, groups: "" });
			}
		}, [client]);

		const handleFieldChange = (
			field: keyof User,
			value: string | boolean | UserGroup[] | string[],
		) => {
			if (props.user) {
				props.setUser?.({ ...props.user, [field]: value });
			}
			props.setErrors?.({ ...props.errors, [field]: "" });
		};

		const setBlankData = () => {
			props.setUser?.({
				contactId: null,
				email: "",
				emailVerified: false,
				enabled: false,
				groups: [],
				id: "",
				username: "",
				firstName: "",
				lastName: "",
			});
		};

		const [deleteUserLogin] = useUserRemoveLoginMutation({
			onCompleted(data) {
				if (data.userRemoveLogin.boolean) {
					setOpenDeleteConformation(false);
					setBlankData();
				}
			},
			onError(error) {
				showNotification(error.message, "error");
			},
		});

		const handleYes = (userId: string) => {
			deleteUserLogin({ variables: { userId } });
		};

		const getListOfContacts = (search: string) => {
			getContacts({
				variables: { first: 10, after: null, name: search },
			});
		};

		return (
			<div className="flex-col gap-5">
				<Box className="fullCenter">
					{user?.id && (
						<FormGroup sx={{ width: "25%" }} className="paddingGroup">
							<FormControlLabel
								control={
									<Switch
										onChange={(event) => handleFieldChange("enabled", event.target.checked)}
										checked={user?.enabled}
									/>
								}
								label={t("login-enabled")}
							/>
						</FormGroup>
					)}
					{user?.id && !props.isDialog && (
						<div className="w-90">
							<Button className="blueButton left" onClick={() => setOpenDeleteConformation(true)}>
								<DeleteIcon />
							</Button>
							<ConformationDialog
								text={t("login.deleteConformation")}
								setOpenDeleteConformation={setOpenDeleteConformation}
								openPopup={openDeleteConformation}
								handleYes={() => handleYes(user?.id?.toString() ?? "")}
							/>
						</div>
					)}
				</Box>
				{!user?.id && !props.contact && (
					<Box className="fullCenter">
						<Dropdown
							dropdownType="contact"
							doNotShowMenu={true}
							elements={contacts}
							getContacts={getListOfContacts}
							element={client}
							setElement={setClient}
							searchPlaceholder={t("select-contact")}
							searchLabel={t("contact")}
							noData={t("nothing-found")}
						/>
					</Box>
				)}
				<Box className="fullCenter">
					{props.isDialog && !user?.id && (
						<TextField
							onChange={(event) => handleFieldChange("username", event.target.value)}
							error={!!props.errors?.username}
							helperText={
								typeof props.errors?.username === "string" ? props.errors.username : undefined
							}
							sx={props.isDialog ? { ml: "left", width: "100%" } : { ml: "left", width: "45%" }}
							value={user?.username}
							label={t("username")}
							variant="outlined"
						/>
					)}
					{!props.isDialog && user?.id && (
						<p>
							<span style={{ fontSize: "25px", marginRight: "25px" }}>
								{t("username").toLocaleLowerCase()}
							</span>
							{user?.username}
						</p>
					)}
				</Box>
				<Box className="fullCenter">
					<Autocomplete
						multiple
						value={groups.filter((group) => {
							return user?.groups.includes(group.id);
						})}
						onChange={(event, newValue) => {
							props.setUser?.({
								__typename: "User",
								id: user?.id ?? "",
								contactId: user?.contactId ?? null,
								email: user?.email ?? "",
								firstName: user?.firstName ?? "",
								lastName: user?.lastName ?? "",
								username: user?.username ?? "",
								emailVerified: user?.emailVerified || false,
								enabled: user?.enabled || false,
								groups: newValue.map((group) => group.id),
							});
							props.setErrors?.({ ...props.errors, groups: "" });
						}}
						options={groups}
						disableCloseOnSelect
						getOptionLabel={(option) => t("groups." + option.name)}
						renderOption={(props, option, { selected }: { selected: boolean }) => {
							const { key, ...optionProps } = props;
							return (
								<li key={key ?? option.id} {...optionProps}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										value={option.id}
										checked={selected}
									/>
									{t("groups." + option.name)}
								</li>
							);
						}}
						className={props.isDialog ? "left w-100" : "left w-45"}
						renderInput={(params) => (
							<TextField
								{...params}
								error={!!props.errors?.groups}
								helperText={
									typeof props.errors?.groups === "string" ? props.errors.groups : undefined
								}
								label={t("groups")}
							/>
						)}
					/>
				</Box>
			</div>
		);
	},
);

UserSection.displayName = "UserSection";
export default UserSection;
